import type { CreatureMechanics, CreatureTrait, CreatureAttack } from '../types/CanonicalSchemas';

/**
 * Pure validators and predicates for embedded creature abilities.
 * Rule reference: SRD 5.1, monster statistics and Combat (2014).
 * date_of_change: 2026-09-05
 * Names and descriptions are intentionally never inspected by these rules.
 */
export const CREATURE_MECHANICS_VERSION = 'srd-5.1-mechanics-2';
export const CREATURE_MECHANICS_DOCUMENTATION = 'Character_Monster_Data_Architecture.md#6-state-machine-and-audit-trail';
export const CREATURE_ABILITIES = ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'] as const;
export const CREATURE_DAMAGE_TYPES = ['acid', 'bludgeoning', 'cold', 'fire', 'force', 'lightning', 'necrotic', 'piercing', 'poison', 'psychic', 'radiant', 'slashing', 'thunder'];
const CONDITIONS = ['blinded', 'charmed', 'deafened', 'exhaustion', 'exhausted', 'frightened', 'grappled', 'incapacitated', 'invisible', 'paralyzed', 'petrified', 'poisoned', 'prone', 'restrained', 'stunned', 'unconscious'];
const BLOCKING = ['incapacitated', 'paralyzed', 'petrified', 'stunned', 'unconscious', 'dead', 'defeated'];

export class CreatureMechanicsError extends Error {
  readonly code = 'CREATURE_MECHANICS_INVALID';
  readonly documentation = CREATURE_MECHANICS_DOCUMENTATION;
  constructor(public entityId: string, public actionId: string, public path: string, public expected: string) {
    super(`${entityId || '(missing entity id)'}/${actionId || '(entity)'}: ${path} requires ${expected}. See ${CREATURE_MECHANICS_DOCUMENTATION}.`);
    this.name = 'CreatureMechanicsError';
  }
}

const object = (value: any) => value && typeof value === 'object' && !Array.isArray(value);
const number = (value: any) => typeof value === 'number' && Number.isFinite(value);
const integer = (value: any) => Number.isInteger(value);
const nonempty = (value: any) => typeof value === 'string' && value.trim().length > 0;
const dice = (value: any) => typeof value === 'string' && /^[1-9]\d*d(?:[2-9]|[1-9]\d+)(?:[+-]\d+)?$/.test(value);

export function validateCreatureMechanics(entry: any, entityId: string, path = 'ability'): void {
  const fail = (suffix: string, expected: string): never => { throw new CreatureMechanicsError(entityId, entry?.id, `${path}${suffix}`, expected); };
  if (!object(entry)) fail('', 'an embedded ability object');
  if (!nonempty(entry.id)) fail('.id', 'a stable ability ID');
  if (!nonempty(entry.name)) fail('.name', 'a display name');
  if (typeof entry.text !== 'string') fail('.text', 'preserved rules or descriptive text');
  const m = entry.mechanics;
  if (!object(m)) fail('.mechanics', 'a structured CreatureMechanics record');
  const shapes: Record<string, string[]> = {
    display: ['kind'],
    attack: ['kind', 'onHit', 'prerequisites'],
    multiattack: ['kind', 'components'],
    standard: ['kind'],
    moveToward: ['kind', 'speedMultiplier', 'requiresVisibleHostile'],
    grantsActions: ['kind', 'actionIds'],
    checkAdvantage: ['kind', 'ability', 'skill', 'senses'],
    adjacentAllyAdvantage: ['kind', 'distanceFeet'],
    extraWeaponDie: ['kind', 'attackTypes', 'count'],
    conditionalDamage: ['kind', 'dice', 'attackTypes', 'allyWithinFeet', 'surpriseRound', 'usage'],
    surviveAtZero: ['kind', 'ability', 'dcBase', 'excludeDamageTypes', 'excludeCritical', 'hpOnSuccess']
  };
  if (!shapes[m.kind]) fail('.mechanics.kind', `one of ${Object.keys(shapes).join(', ')}`);
  Object.keys(m).forEach(key => { if (!shapes[m.kind].includes(key)) fail(`.mechanics.${key}`, `only ${shapes[m.kind].join(', ')}`); });
  const positive = (value: any, suffix: string) => { if (!number(value) || value <= 0) fail(suffix, 'a positive number'); };
  const stringList = (value: any, suffix: string, allowed?: readonly string[]) => {
    if (!Array.isArray(value) || !value.length || value.some(v => !nonempty(v) || (allowed && !allowed.includes(v)))) fail(suffix, allowed ? `a nonempty array of ${allowed.join(', ')}` : 'a nonempty array of IDs');
  };
  if (m.kind === 'attack') {
    const attack: CreatureAttack = entry.attack;
    if (!object(attack)) fail('.attack', 'a structured CreatureAttack');
    if (!['meleeWeapon', 'rangedWeapon', 'meleeSpell', 'rangedSpell'].includes(attack.type)) fail('.attack.type', 'an explicit meleeWeapon, rangedWeapon, meleeSpell or rangedSpell');
    if (!integer(attack.toHit)) fail('.attack.toHit', 'an integer attack bonus');
    if (attack.type.startsWith('melee')) positive(attack.reach, '.attack.reach');
    else {
      positive(attack.range?.normal, '.attack.range.normal');
      positive(attack.range?.long, '.attack.range.long');
      if (attack.range!.long < attack.range!.normal) fail('.attack.range.long', 'a range at least as large as normal range');
    }
    if (!Array.isArray(attack.damage) || !attack.damage.length) fail('.attack.damage', 'at least one typed damage formula');
    attack.damage.forEach((d, i) => {
      if (!dice(d?.dice)) fail(`.attack.damage[${i}].dice`, 'NdM with an optional signed integer modifier');
      if (!CREATURE_DAMAGE_TYPES.includes(d?.type)) fail(`.attack.damage[${i}].type`, 'a canonical damage type');
    });
    if (!Array.isArray(m.onHit)) fail('.mechanics.onHit', 'an explicit array of saveCondition riders, including []');
    m.onHit.forEach((r: any, i: number) => {
      const p = `.mechanics.onHit[${i}]`;
      if (!object(r) || r.kind !== 'saveCondition' || Object.keys(r).some(k => !['kind', 'ability', 'dc', 'condition'].includes(k))) fail(p, 'only {kind: saveCondition, ability, dc, condition}');
      if (!CREATURE_ABILITIES.includes(r.ability)) fail(`${p}.ability`, 'a canonical long-form ability');
      if (!integer(r.dc) || r.dc < 1) fail(`${p}.dc`, 'a positive integer save DC');
      if (!CONDITIONS.includes(r.condition)) fail(`${p}.condition`, 'a canonical condition');
    });
    if (!Array.isArray(m.prerequisites)) fail('.mechanics.prerequisites', 'an explicit prerequisite array, including []');
    m.prerequisites.forEach((p: any, i: number) => {
      if (!object(p) || p.kind !== 'noShield' || Object.keys(p).some(k => k !== 'kind')) fail(`.mechanics.prerequisites[${i}]`, '{kind: noShield}');
    });
  } else if (m.kind === 'multiattack') {
    if (!Array.isArray(m.components) || !m.components.length) fail('.mechanics.components', 'a nonempty ordered array of {actionId, count}');
    m.components.forEach((c: any, i: number) => {
      if (!object(c) || !nonempty(c.actionId) || !integer(c.count) || c.count < 1 || c.count > 20 || Object.keys(c).some(k => !['actionId', 'count'].includes(k))) fail(`.mechanics.components[${i}]`, '{actionId: stable ID, count: integer 1..20}');
    });
  } else if (m.kind === 'grantsActions') stringList(m.actionIds, '.mechanics.actionIds');
  else if (m.kind === 'checkAdvantage') {
    if (!CREATURE_ABILITIES.includes(m.ability) || m.skill !== 'perception') fail('.mechanics', 'a canonical ability and skill: perception');
    stringList(m.senses, '.mechanics.senses', ['sight', 'hearing', 'smell']);
  } else if (m.kind === 'adjacentAllyAdvantage') positive(m.distanceFeet, '.mechanics.distanceFeet');
  else if (m.kind === 'extraWeaponDie' || m.kind === 'conditionalDamage') {
    stringList(m.attackTypes, '.mechanics.attackTypes', ['meleeWeapon', 'rangedWeapon']);
    if (m.kind === 'extraWeaponDie' && (!integer(m.count) || m.count < 1)) fail('.mechanics.count', 'a positive integer');
    if (m.kind === 'conditionalDamage') {
      if (!dice(m.dice)) fail('.mechanics.dice', 'a damage dice expression');
      if (m.allyWithinFeet !== undefined) positive(m.allyWithinFeet, '.mechanics.allyWithinFeet');
      if (m.surpriseRound !== undefined && (!integer(m.surpriseRound) || m.surpriseRound < 1)) fail('.mechanics.surpriseRound', 'a positive integer round');
      if (m.usage !== undefined && (!object(m.usage) || m.usage.limit !== 1 || m.usage.reset !== 'turn' || Object.keys(m.usage).some(k => !['limit', 'reset'].includes(k)))) fail('.mechanics.usage', '{limit: 1, reset: turn}');
    }
  } else if (m.kind === 'surviveAtZero') {
    if (!CREATURE_ABILITIES.includes(m.ability)) fail('.mechanics.ability', 'a canonical long-form ability');
    if (!integer(m.dcBase) || m.dcBase < 0) fail('.mechanics.dcBase', 'a nonnegative integer');
    stringList(m.excludeDamageTypes, '.mechanics.excludeDamageTypes', CREATURE_DAMAGE_TYPES);
    if (typeof m.excludeCritical !== 'boolean') fail('.mechanics.excludeCritical', 'an explicit boolean');
    if (!integer(m.hpOnSuccess) || m.hpOnSuccess < 1) fail('.mechanics.hpOnSuccess', 'a positive integer');
  } else if (m.kind === 'moveToward') {
    positive(m.speedMultiplier, '.mechanics.speedMultiplier');
    if (m.requiresVisibleHostile !== true) fail('.mechanics.requiresVisibleHostile', 'true');
  }
  if (['attack', 'multiattack', 'standard', 'moveToward'].includes(m.kind)) {
    if (!['action', 'bonus action', 'reaction'].includes(entry.activationType)) fail('.activationType', 'action, bonus action or reaction');
    const route = m.kind === 'standard' ? 'standard' : (m.kind === 'moveToward' ? 'ability' : 'attack');
    if (entry.executionRoute !== route) fail('.executionRoute', route);
    if (!['none', 'self', 'creature', 'object'].includes(entry.targetRequirement)) fail('.targetRequirement', 'an explicit target requirement');
    if (m.kind === 'standard' && !['bonus-action-hide', 'bonus-action-disengage'].includes(entry.standardActionKey)) fail('.standardActionKey', 'an implemented standard-action key');
  } else if (!['passive', 'trigger'].includes(entry.activation)) fail('.activation', 'passive or trigger');
}

/** Validate the complete mechanical inventory before any action, roll or mutation. */
export function validateCreatureInventory(data: any, entityId: string): void {
  const fail = (path: string, expected: string): never => { throw new CreatureMechanicsError(entityId, '', path, expected); };
  if (data?.contentVersion !== CREATURE_MECHANICS_VERSION) fail('contentVersion', CREATURE_MECHANICS_VERSION);
  const entries: any[] = [];
  for (const key of ['actions', 'bonusActions', 'reactions', 'traits']) {
    if (!Array.isArray(data[key])) fail(key, 'an explicit array, including []');
    data[key].forEach((entry: any, index: number) => { validateCreatureMechanics(entry, entityId, `${key}[${index}]`); entries.push(entry); });
  }
  const byId = new Map(entries.map(e => [e.id, e]));
  if (byId.size !== entries.length) fail('abilities.id', 'unique IDs across actions and traits');
  entries.forEach(entry => {
    const m = entry.mechanics;
    const refs = m.kind === 'multiattack' ? m.components.map((c: any) => c.actionId) : m.kind === 'grantsActions' ? m.actionIds : [];
    refs.forEach((ref: string) => {
      const target = byId.get(ref);
      if (!target || !['action', 'bonus action', 'reaction'].includes(target.activationType) || (m.kind === 'multiattack' && (target.mechanics.kind !== 'attack' || target.activationType !== 'action'))) throw new CreatureMechanicsError(entityId, entry.id, 'mechanics.actionId', `an embedded ${m.kind === 'multiattack' ? 'main attack' : 'action'} ID; missing/illegal ${ref}`);
    });
  });
  for (const key of ['resist', 'immune', 'vuln']) {
    const list = data.resistanceBlock?.[key];
    if (!Array.isArray(list) || list.some(t => !CREATURE_DAMAGE_TYPES.includes(t))) fail(`resistanceBlock.${key}`, 'an explicit array of canonical damage types');
  }
  if (!Array.isArray(data.conditionImmunities) || data.conditionImmunities.some((c: string) => !CONDITIONS.includes(c))) fail('conditionImmunities', 'an explicit array of canonical conditions');
  if (!object(data.savingThrows) || CREATURE_ABILITIES.some(k => !integer(data.savingThrows[k]))) fail('savingThrows', 'all six long-form ability keys with integer total modifiers');
  if (!object(data.equipmentState) || typeof data.equipmentState.shieldEquipped !== 'boolean') fail('equipmentState.shieldEquipped', 'an explicit boolean');
  if (!integer(data.equipmentState.shieldArmorClassBonus) || data.equipmentState.shieldArmorClassBonus < 0 || data.equipmentState.shieldArmorClassBonus > 10) fail('equipmentState.shieldArmorClassBonus', 'an explicit integer from 0 through 10');
  if (data.equipmentState.shieldEquipped && data.equipmentState.shieldArmorClassBonus === 0) fail('equipmentState', 'a positive shieldArmorClassBonus when shieldEquipped is true');
}

export function creatureConditions(actor: any): string[] {
  return [...(Array.isArray(actor?.conditions) ? actor.conditions : []), ...Object.entries(actor?.statusEffects || {}).filter(([, active]) => active === true).map(([key]) => key)]
    .map(value => String(value?.name ?? value).toLowerCase());
}

export function creatureCanAct(actor: any): boolean {
  return number(actor?.hp) && actor.hp > 0 && !creatureConditions(actor).some(c => BLOCKING.includes(c));
}

export function creatureAbilityTraits(actor: any): CreatureTrait[] {
  return Array.isArray(actor?.traits) ? actor.traits : [];
}

export function creatureCheckAdvantage(actor: any, check: { ability?: string; skill?: string; sense?: string }): string[] {
  return creatureAbilityTraits(actor).filter(trait => {
    const m = trait.mechanics;
    return m?.kind === 'checkAdvantage' && m.ability === check.ability && m.skill === check.skill && m.senses.includes(check.sense as any);
  }).map(trait => trait.id);
}

export function creatureActionPrerequisite(actor: any, action: any): string | null {
  const prerequisites = action?.mechanics?.kind === 'attack' ? action.mechanics.prerequisites : [];
  for (const prerequisite of prerequisites || []) {
    if (prerequisite.kind === 'noShield') {
      if (typeof actor?.equipmentState?.shieldEquipped !== 'boolean') return 'Set equipmentState.shieldEquipped before using this weapon mode.';
      if (actor.equipmentState.shieldEquipped) return 'This two-handed weapon mode requires the shield to be doffed.';
    }
  }
  return null;
}

/** Shared trigger predicate for manual prompts and authoritative replay.
 * SRD 5.1 Undead Fortitude; date_of_change: 2026-09-05
 */
export function creatureZeroHpSavePlan(actor: any, input: { hpBefore: number; hpAfter: number; damage: number; damageTypes: string[]; critical: boolean }): any[] {
  if (input.hpBefore <= 0 || input.hpAfter !== 0 || input.damage <= 0) return [];
  return creatureAbilityTraits(actor).flatMap(trait => {
    const m = trait.mechanics;
    if (m?.kind !== 'surviveAtZero') return [];
    return [{ abilityId: trait.id, ability: m.ability, dc: m.dcBase + input.damage,
      excluded: (m.excludeCritical && input.critical) || m.excludeDamageTypes.some(type => input.damageTypes.includes(type)) }];
  });
}

/** SRD 5.1 Undead Fortitude: triggering damage is the post-mitigation packet, not HP lost. */
export function resolveCreatureZeroHp(actor: any, input: { hpBefore: number; hpAfter: number; damage: number; damageTypes: string[]; critical: boolean }, roll: (sides: number, abilityId: string) => number) {
  const saves: any[] = [];
  let hpAfter = input.hpAfter;
  const plannedSaves = creatureZeroHpSavePlan(actor, input);
  for (const trait of creatureAbilityTraits(actor)) {
    const m = trait.mechanics;
    const planned = plannedSaves.find(save => save.abilityId === trait.id);
    if (m.kind !== 'surviveAtZero' || !planned || hpAfter !== 0) continue;
    if (planned.excluded) {
      saves.push({ abilityId: trait.id, excluded: true, ruleReference: trait.ruleReference });
      continue;
    }
    const modifier = actor.savingThrows?.[m.ability];
    if (!integer(modifier)) throw new CreatureMechanicsError(actor.id, trait.id, `savingThrows.${m.ability}`, 'an integer total saving throw modifier');
    const dc = m.dcBase + input.damage;
    const d20 = roll(20, trait.id);
    if (!integer(d20) || d20 < 1 || d20 > 20) throw new CreatureMechanicsError(actor.id, trait.id, 'roll', 'a d20 face from 1 to 20');
    const total = d20 + modifier;
    const success = total >= dc;
    if (success) hpAfter = m.hpOnSuccess;
    saves.push({ abilityId: trait.id, ability: m.ability, formula: `1d20${modifier >= 0 ? '+' : ''}${modifier}`, rolls: [d20], modifier, total, dc, success, hpAfter, ruleReference: trait.ruleReference });
  }
  return { hpAfter, saves };
}
